import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import AddDialogStyle from "styles/application/AddDialogStyle";
import DynamicDropdown from "components/Elements/DynamicDropdown";
import { getChangeStatusReasons, restoreAuditEntity } from "services/audits";
import { entityTypeFor } from "./entityRenderers";

const ADD = AddDialogStyle();

const AuditRestoreDialog = ({ row, onClose }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [reasons, setReasons] = useState([]);
	const [selectedReason, setSelectedReason] = useState({});
	const [otherReason, setOtherReason] = useState("");
	const [duplicateWarning, setDuplicateWarning] = useState(null);
	const [error, setError] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);

	const isService = row.entityType === "service";
	const typeLabel = entityTypeFor(row.entityType).label(t, row.entityType);
	const reference = row.entityReference || `#${row.entityId}`;

	useEffect(() => {
		if (!isService) return;
		let active = true;
		getChangeStatusReasons().then((response) => {
			if (active && Array.isArray(response?.data)) setReasons(response.data);
		});
		return () => {
			active = false;
		};
	}, [isService]);

	const handleRestore = async (confirmDuplicate = false) => {
		setSubmitting(true);
		setError(null);

		const response = await restoreAuditEntity({
			entityType: row.entityType,
			entityId: row.entityId,
			changeStatusReasonId: selectedReason?.id || null,
			otherReason: otherReason || null,
			confirmDuplicate,
		});

		setSubmitting(false);

		if (response.status) {
			queryClient.invalidateQueries({ queryKey: ["audits"] });
			const { detailPath } = entityTypeFor(row.entityType);
			if (detailPath && row.linkEntityId) {
				window.open(detailPath(row.linkEntityId), "_blank", "noopener,noreferrer");
			}
			onClose();
			return;
		}

		if (response.data?.requiresConfirmation) {
			setDuplicateWarning(response.data.message);
			return;
		}

		setError(response.data?.detail || response.data?.title || t("message.restoreFailed"));
	};

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="sm">
			<ADD.ActionContainer>
				<DialogTitle>
					<ADD.HeaderText>{t("title.restoreEntity", { entity: typeLabel })}</ADD.HeaderText>
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton onClick={onClose} variant="contained" disabled={isSubmitting}>
						{t("action.cancel")}
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={() => handleRestore(Boolean(duplicateWarning))}
						variant="contained"
						disabled={isSubmitting}
					>
						{duplicateWarning ? t("action.restoreAnyway") : t("action.restore")}
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent>
				<Typography style={{ marginBottom: 12 }}>
					{isService
						? t("message.restoreReturnsScheduled", { reference })
						: t("message.restoreReturnsPreviousStatus", { reference })}
				</Typography>
				{isService && (
					<div style={{ marginBottom: 12 }}>
						<DynamicDropdown
							dataSource={reasons}
							columns={[{ name: "name", id: 1 }]}
							showHeader={false}
							width="100%"
							placeholder={t("placeholder.selectReason")}
							onChange={(item) => setSelectedReason(item)}
							selectdValueToshow="name"
							selectedValue={selectedReason}
							label={t("label.restoreReason")}
							required={false}
							showBorderColor
						/>
					</div>
				)}
				{isService && (
					<TextField
						label={t("label.notes")}
						fullWidth
						multiline
						value={otherReason}
						onChange={(e) => setOtherReason(e.target.value)}
					/>
				)}
				{duplicateWarning && (
					<Typography color="error" style={{ marginTop: 12 }}>
						{duplicateWarning}
					</Typography>
				)}
				{error && (
					<Typography color="error" style={{ marginTop: 12 }}>
						{error}
					</Typography>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default AuditRestoreDialog;
